import {NgClass} from '@angular/common';
import {Component} from '@angular/core';
import {
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {validators} from '@emma-labs/shared/angular';
import {ErrorResponse, ErrorResponseCode} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Button} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {PasswordModule} from 'primeng/password';
import {debounceTime, first} from 'rxjs';
import {BrokerProfileService} from '../../../services/broker-profile/broker-profile.service';
import {DEBOUNCE_TIME} from '../../../utils/utility-constants';

interface PasswordForm {
  currentPassword: FormControl<string>;
  newPassword: FormControl<string>;
  confirmNewPassword: FormControl<string>;
}

@UntilDestroy()
@Component({
  selector: 'em-password',
  templateUrl: './password.component.html',
  imports: [CardModule, ReactiveFormsModule, NgClass, PasswordModule, Button],
})
export class PasswordComponent {
  form: FormGroup<PasswordForm>;
  isLoading = false;
  isSuccess = false;
  isIncorrectPasswordError = false;
  isPasswordTooWeakError = false;

  constructor(
    private readonly profileService: BrokerProfileService,
    private readonly fb: NonNullableFormBuilder,
  ) {
    this.form = this.createForm();
  }

  updatePassword(): void {
    this.isLoading = true;

    this.profileService
      .updatePassword(this.form.getRawValue())
      .pipe(first())
      .subscribe({
        next: () => {
          this.isSuccess = true;
          this.isLoading = false;
          this.form.reset(this.form.value, {emitEvent: false});
        },
        error: (error: ErrorResponse) => {
          switch (error.code) {
            case ErrorResponseCode.INCORRECT_PASSWORD:
              this.isIncorrectPasswordError = true;
              break;
            case ErrorResponseCode.PASSWORD_TOO_WEAK:
              this.isPasswordTooWeakError = true;
              break;
          }
          this.isLoading = false;
        },
      });
  }

  hasPasswordError(errorCode: string): boolean {
    return (
      this.form.controls.newPassword.hasError(errorCode) ||
      this.form.controls.newPassword.hasError('required')
    );
  }

  private createForm(): FormGroup<PasswordForm> {
    const form = this.fb.group(
      {
        currentPassword: ['', Validators.required],
        newPassword: ['', [Validators.required, validators.password]],
        confirmNewPassword: ['', [Validators.required, validators.password]],
      },
      {
        validators: [validators.passwordConfirmation],
      },
    );

    form.valueChanges
      .pipe(debounceTime(DEBOUNCE_TIME), untilDestroyed(this))
      .subscribe(() => this.resetErrors());

    return form;
  }

  private resetErrors(): void {
    this.isSuccess = false;
    this.isIncorrectPasswordError = false;
    this.isPasswordTooWeakError = false;
  }
}
